/** The service catalog domain.
 *
 * The catalog is the one part of this product an end user actually chooses to
 * visit, so it is worth being precise about what the backend can serve.
 *
 * `GET /api/portal/catalog/` returns, per catalog item: `id`, `name`, `service`
 * and `form_schema`. That is the whole payload. The underlying
 * `ServiceSubcategory` model also carries `default_sla`, `default_team` and
 * `active`, none of which is serialized — so three of the fields the catalog
 * needs are one serializer change away, while the rest need new model fields.
 *
 * `FIELD_TYPES` and `WORKFLOW` below are the honest inventories, read by both
 * the portal catalog and the admin screens.
 */

export interface CatalogEntry {
  id: number
  /** The catalog item — "Laptop fault", "Software install request". */
  name: string
  /** The owning service, which is the catalog's real category axis today. */
  service: string
  form_schema: { fields?: CatalogField[] }
}

export interface CatalogField {
  name: string
  label?: string
  type?: string
  required?: boolean
  options?: string[]
}

/* ------------------------------------------------------------- categories */

export interface CatalogCategory {
  /** The service name, used as the URL key and the group heading. */
  key: string
  label: string
  items: CatalogEntry[]
}

/** Group catalog items by their service.
 *
 *  Service is the only grouping the data has. The ten named categories the
 *  brief asks for — Hardware, Software, Microsoft 365, Access, Network,
 *  Security, HR, Facilities, Finance, Cloud — are a different taxonomy that
 *  needs its own model with an icon and a description. Mapping the seeded
 *  services onto those names by guesswork would put items in categories nobody
 *  chose, so the real services are shown and the target taxonomy is documented.
 */
export function groupByService(items: CatalogEntry[]): CatalogCategory[] {
  const groups = new Map<string, CatalogEntry[]>()
  for (const item of items) {
    groups.set(item.service, [...(groups.get(item.service) ?? []), item])
  }
  return [...groups.entries()]
    .map(([key, entries]) => ({
      key,
      label: key,
      items: [...entries].sort((a, b) => a.name.localeCompare(b.name)),
    }))
    .sort((a, b) => a.label.localeCompare(b.label))
}

/** Free-text search across item name, service and field labels.
 *
 *  Field labels are included because people search for the thing they need to
 *  supply — somebody looking for "asset tag" should find the laptop fault form
 *  that asks for one.
 */
export function searchCatalog(items: CatalogEntry[], term: string): CatalogEntry[] {
  const needle = term.trim().toLowerCase()
  if (!needle) return items
  return items.filter((item) => {
    if (item.name.toLowerCase().includes(needle)) return true
    if (item.service.toLowerCase().includes(needle)) return true
    return (item.form_schema?.fields ?? []).some((field) =>
      (field.label ?? field.name).toLowerCase().includes(needle),
    )
  })
}

/** Other items in the same service. Genuinely related — same owning team, same
 *  catalog branch — rather than a recommendation engine we do not have. */
export function relatedItems(items: CatalogEntry[], current: CatalogEntry, limit = 4): CatalogEntry[] {
  return items
    .filter((item) => item.service === current.service && item.id !== current.id)
    .slice(0, limit)
}

/* ------------------------------------------------------------ field types */

export interface FieldTypeSpec {
  key: string
  label: string
  /** True when the portal renderer draws it AND the server validates it. */
  supported: boolean
  note: string
}

/** Every field type the catalog is asked to support.
 *
 *  The `supported` flag is deliberately strict: it means the portal renders the
 *  control *and* `tickets/form_schema.py` validates a submission containing it.
 *  A control the client can draw but the server rejects is worse than no
 *  control, because the user only finds out after filling the form in.
 */
export const FIELD_TYPES: FieldTypeSpec[] = [
  { key: 'text', label: 'Text', supported: true, note: 'Single line.' },
  { key: 'textarea', label: 'Long text', supported: true, note: 'Multi-line.' },
  { key: 'select', label: 'Dropdown', supported: true, note: 'Fixed options from the schema.' },
  { key: 'number', label: 'Number', supported: true, note: 'Numeric input.' },
  { key: 'date', label: 'Date', supported: true, note: 'Native date picker.' },
  { key: 'checkbox', label: 'Checkbox', supported: true, note: 'Boolean.' },
  {
    key: 'people',
    label: 'People picker',
    supported: false,
    note: 'Needs a contact search endpoint. Contacts exist but nothing exposes a searchable directory to the portal, and doing so has a privacy decision attached.',
  },
  {
    key: 'department',
    label: 'Department',
    supported: false,
    note: 'No department field exists on the contact — the same gap Reports and Assets hit.',
  },
  {
    key: 'manager',
    label: 'Manager',
    supported: false,
    note: 'No manager relationship on the contact. Entra ID holds one and Graph could supply it.',
  },
  {
    key: 'attachment',
    label: 'Attachment',
    supported: false,
    note: 'Attachments exist on tickets and are served through a permission-checking view, but nothing accepts a file during creation — it would have to upload after the ticket exists.',
  },
  {
    key: 'asset',
    label: 'Asset picker',
    supported: false,
    note: 'The CMDB is agent-only. A portal user cannot list configuration items, and exposing the estate to end users needs its own scoping rules.',
  },
]

export const SUPPORTED_FIELD_TYPES = FIELD_TYPES.filter((type) => type.supported)
export const UNSUPPORTED_FIELD_TYPES = FIELD_TYPES.filter((type) => !type.supported)

/** Which of a schema's fields the portal can actually render and submit. */
export function schemaSupport(fields: CatalogField[]): {
  total: number
  supported: number
  unsupported: CatalogField[]
} {
  const known = new Set(SUPPORTED_FIELD_TYPES.map((type) => type.key))
  // A field with no type is a text field — that is the renderer's default and
  // the server's, so an untyped field is supported rather than unknown.
  const unsupported = fields.filter((field) => field.type && !known.has(field.type))
  return { total: fields.length, supported: fields.length - unsupported.length, unsupported }
}

/* -------------------------------------------------------------- workflow */

export type StageSupport = 'backed' | 'partial' | 'absent'

export interface WorkflowStage {
  key: string
  label: string
  support: StageSupport
  note: string
}

/** The fulfilment path a request takes, and how much of it is real.
 *
 *  Drawn on both the portal item page and the admin screen so a requester and a
 *  service owner see the same promise. Approval is the honest weak point: the
 *  product has an approval model, but it belongs to changes, and no catalog
 *  item can require one.
 */
export const WORKFLOW: WorkflowStage[] = [
  {
    key: 'request',
    label: 'Request',
    support: 'backed',
    note: 'The requester submits the form. Identity comes from the session, never the payload.',
  },
  {
    key: 'approval',
    label: 'Approval',
    support: 'absent',
    note: 'Approval exists for change requests only. A catalog item cannot require one, so no request is ever held for a manager.',
  },
  {
    key: 'assignment',
    label: 'Assignment',
    support: 'partial',
    note: 'ServiceSubcategory.default_team routes the ticket on creation, but it is not serialized, so the portal cannot say who will pick it up.',
  },
  {
    key: 'fulfilment',
    label: 'Fulfilment',
    support: 'backed',
    note: 'A technician works the ticket in the console, against the SLA clock.',
  },
  {
    key: 'closed',
    label: 'Completed',
    support: 'backed',
    note: 'Resolved, then closed, with the reopen window and a satisfaction score.',
  },
]

/* -------------------------------------------- the target category taxonomy */

/** The ten categories the catalog is asked to present.
 *
 *  Recorded as a specification, not as data. Nothing in the model carries an
 *  icon, a description or an ordering, so this list is what a `CatalogCategory`
 *  model would need to contain — see docs/UI-BACKLOG.md.
 */
export const TARGET_CATEGORIES = [
  'Hardware',
  'Software',
  'Microsoft 365',
  'Access Requests',
  'Network',
  'Security',
  'HR',
  'Facilities',
  'Finance',
  'Cloud Services',
] as const

/** Item-level fields the catalog is asked to show, and where each would come
 *  from. Three are a serializer change; the rest need new columns. */
export const ITEM_FIELDS = [
  { key: 'name', label: 'Name', backed: true, source: 'ServiceSubcategory.name' },
  { key: 'service', label: 'Category', backed: true, source: 'Service.name' },
  { key: 'form_schema', label: 'Required information', backed: true, source: 'ServiceSubcategory.form_schema' },
  { key: 'sla', label: 'SLA', backed: false, source: 'ServiceSubcategory.default_sla exists — add it to PortalSubcategorySerializer' },
  { key: 'fulfilment_team', label: 'Fulfilment group', backed: false, source: 'ServiceSubcategory.default_team exists — add it to the serializer' },
  { key: 'active', label: 'Published', backed: false, source: 'ServiceSubcategory.active exists — add it to the serializer' },
  { key: 'description', label: 'Description', backed: false, source: 'ServiceSubcategory.description — new field' },
  { key: 'icon', label: 'Icon', backed: false, source: 'ServiceSubcategory.icon — new field, a named icon rather than an upload' },
  { key: 'fulfilment_time', label: 'Expected fulfilment time', backed: false, source: 'Derivable from default_sla.resolution_mins once the SLA is serialized' },
  { key: 'approval', label: 'Approval required', backed: false, source: 'ServiceSubcategory.requires_approval plus an approver rule — no catalog approval exists' },
  { key: 'cost', label: 'Estimated cost', backed: false, source: 'ServiceSubcategory.estimated_cost, currency — new fields' },
  { key: 'kb_articles', label: 'Knowledge articles', backed: false, source: 'A link table between catalog items and KB articles. Title search is a stand-in, not a curated list.' },
] as const
